import { useCallback, useState } from "react";
import { trpc } from "../../trpc";
import { runConcurrentTasks } from "../../../shared/runConcurrentTasks";
import { runWithAutoRetry } from "../../../shared/runWithAutoRetry";
import { IMAGE_UNDERSTANDING_CONCURRENCY } from "../../../shared/feishuScriptSheet";
import type { ScriptSheetImageRef } from "../../../shared/feishuScriptSheet";
import type { ImageUnderstandingState, SheetReadResult, SimpleActiveItem } from "./types";

export interface ImageUnderstandingCallbacks {
  onProgress?: (input: {
    done: number;
    total: number;
    activeItems: SimpleActiveItem[];
  }) => void;
  onStepError?: (error: string, retryCountdown?: number) => void;
}

export function useScriptImageUnderstanding() {
  const [running, setRunning] = useState(false);

  const understandAllImages = useCallback(
    async (
      input: {
        result: SheetReadResult;
        images: ScriptSheetImageRef[];
        imageUnderstandings: Record<string, ImageUnderstandingState>;
        setImageUnderstandings: React.Dispatch<
          React.SetStateAction<Record<string, ImageUnderstandingState>>
        >;
      },
      callbacks?: ImageUnderstandingCallbacks
    ): Promise<Record<string, string>> => {
      const { result, images, imageUnderstandings, setImageUnderstandings } = input;
      const texts: Record<string, string> = {};
      for (const [fileToken, item] of Object.entries(imageUnderstandings)) {
        if (item.content?.trim()) texts[fileToken] = item.content.trim();
      }
      if (images.length === 0) return texts;

      setRunning(true);
      const pending = new Map(images.map((item) => [item.fileToken, item]));

      try {
        await runWithAutoRetry(
          async () => {
            const items = [...pending.values()];
            if (items.length === 0) return;

            const failures: Array<{ fileToken: string; error: string }> = [];

            await runConcurrentTasks({
              items,
              concurrency: IMAGE_UNDERSTANDING_CONCURRENCY,
              worker: async (item) => {
                try {
                  const understanding = await trpc.feishuSheets.understandImage.mutate({
                    fileToken: item.fileToken,
                    spreadsheetToken: result.spreadsheetToken,
                    width: item.width,
                    height: item.height,
                  });
                  if (!understanding.content?.trim()) {
                    return {
                      ok: false as const,
                      item,
                      error: "图片理解结果为空",
                    };
                  }
                  return { ok: true as const, item, understanding };
                } catch (error) {
                  return {
                    ok: false as const,
                    item,
                    error: error instanceof Error ? error.message : "图片理解失败",
                  };
                }
              },
              onItemStart: ({ item }) => {
                callbacks?.onProgress?.({
                  done: images.length - pending.size,
                  total: images.length,
                  activeItems: [
                    {
                      key: item.fileToken,
                      label: `正在理解第 ${item.rowNumber} 行图片`,
                    },
                  ],
                });
              },
              onItemComplete: ({ item, done, result: taskResult }) => {
                if (taskResult.ok) {
                  pending.delete(item.fileToken);
                  texts[item.fileToken] = taskResult.understanding.content.trim();
                  setImageUnderstandings((prev) => ({
                    ...prev,
                    [item.fileToken]: {
                      content: taskResult.understanding.content,
                      cached: taskResult.understanding.cached,
                      understoodAt: taskResult.understanding.understoodAt,
                    },
                  }));
                } else {
                  failures.push({
                    fileToken: item.fileToken,
                    error: taskResult.error,
                  });
                }

                const activeItems: SimpleActiveItem[] = failures.length
                  ? [
                      {
                        key: failures[0]!.fileToken,
                        label: `第 ${item.rowNumber} 行图片理解失败：${taskResult.ok ? "" : taskResult.error}`,
                      },
                    ]
                  : pending.size > 0
                    ? [
                        {
                          key: "next",
                          label: `正在处理剩余 ${pending.size} 张图片…`,
                        },
                      ]
                    : [];

                callbacks?.onProgress?.({
                  done,
                  total: images.length,
                  activeItems,
                });
              },
            });

            if (failures.length > 0) {
              const first = failures[0]!;
              const item = images.find((img) => img.fileToken === first.fileToken);
              throw new Error(
                item
                  ? `第 ${item.rowNumber} 行图片理解失败：${first.error}`
                  : first.error
              );
            }
          },
          {
            delayMs: 5000,
            maxAttempts: 10,
            onRetry: (_attempt, error) => {
              const msg = error instanceof Error ? error.message : "图片理解失败";
              callbacks?.onStepError?.(msg);
            },
            onCountdown: (secondsLeft) => {
              const first = images.find((img) => pending.has(img.fileToken));
              callbacks?.onStepError?.(
                first
                  ? `第 ${first.rowNumber} 行图片理解失败，即将自动重试`
                  : "图片理解失败，即将自动重试",
                secondsLeft
              );
            },
          }
        );
        return texts;
      } finally {
        setRunning(false);
      }
    },
    []
  );

  return { running, understandAllImages };
}
